import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";
import {
  GoogleLogo,
  CheckCircle,
  Warning,
  ArrowSquareOut,
  Link as LinkIcon,
  Star,
  Copy,
  Lightning,
  Lock,
  Info,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReviewSettings() {
  const navigate = useNavigate();
  const { isEntitled, isFeatureActive } = useEntitlementsWithFetch();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [googleUrl, setGoogleUrl] = useState("");
  
  // Check entitlements - but DON'T block page access
  // ACTION-BASED GATING: Configuration is allowed, only SEND actions are gated
  const reviewFeatureActive = isFeatureActive("review_collection");
  const hasReviewEntitlement = isEntitled("review_collection");

  useEffect(() => {
    // Load configuration data regardless of entitlement
    // Users can configure settings, gating happens on send actions
    fetchGoogleBusinessInfo();
  }, []);

  const fetchGoogleBusinessInfo = async () => {
    try {
      const response = await fetch(`${API}/reviews/google-business/search`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setSearchData(data);
        if (data.current_url) {
          setGoogleUrl(data.current_url);
        }
      }
    } catch (error) {
      console.error("Error fetching Google Business info:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveGoogleUrl = async () => {
    if (!googleUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    // Basic URL validation
    if (!googleUrl.startsWith("http://") && !googleUrl.startsWith("https://")) {
      toast.error("Please enter a valid URL starting with http:// or https://");
      return;
    }

    setSaving(true);
    try {
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", `${API}/reviews/google-business/save`);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.withCredentials = true;
        xhr.onload = () => {
          try {
            const data = JSON.parse(xhr.responseText);
            resolve({ status: xhr.status, data });
          } catch {
            resolve({ status: xhr.status, data: { detail: xhr.responseText } });
          }
        };
        xhr.onerror = () => reject(new Error("Network error"));
        xhr.send(JSON.stringify({ google_business_url: googleUrl }));
      });

      if (result.status === 200) {
        toast.success("URL saved successfully!");
        fetchGoogleBusinessInfo();
      } else {
        toast.error(result.data.detail || "Failed to save URL");
      }
    } catch (error) {
      console.error("Save Google URL error:", error);
      toast.error("Failed to save URL");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-48 bg-zinc-100 rounded animate-pulse" />
        <div className="h-64 bg-zinc-100 rounded animate-pulse" />
      </div>
    );
  }

  // ACTION-BASED GATING: Page-level blocking removed
  // Users can configure review settings without entitlement
  // Gating happens only on SEND review request actions (in CustomerDetail.js)

  return (
    <div className="space-y-6" data-testid="review-settings">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold uppercase">Review Collection</h1>
        <p className="text-zinc-500">Configure Google Reviews and collect customer feedback</p>
      </div>

      {/* Status Card */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-50 border-2 border-blue-200 flex items-center justify-center rounded-sm">
                <GoogleLogo size={28} className="text-blue-600" weight="bold" />
              </div>
              <div>
                <CardTitle className="text-lg">Google Business Profile</CardTitle>
                <CardDescription>Connect your Google Business listing to collect reviews</CardDescription>
              </div>
            </div>
            {searchData?.current_url ? (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-300">
                <CheckCircle size={14} className="mr-1" />
                Connected
              </Badge>
            ) : (
              <Badge className="bg-amber-50 text-amber-700 border-amber-300">
                <Warning size={14} className="mr-1" />
                Not Configured
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {searchData?.current_url && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800 font-medium mb-2">Your current URL:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-white p-2 rounded border border-emerald-200 text-emerald-700 truncate">
                  {searchData.current_url}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(searchData.current_url);
                    toast.success("URL copied!");
                  }}
                >
                  <Copy size={16} />
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <a href={searchData.current_url} target="_blank" rel="noopener noreferrer">
                    <ArrowSquareOut size={16} />
                  </a>
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions Card */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info size={20} />
            How to Find Your Google Business URL
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Business Info */}
          {searchData?.tenant_info && (
            <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-lg">
              <p className="text-sm font-medium text-zinc-600 mb-2">Your Business Information:</p>
              <div className="space-y-1 text-sm">
                <p><strong>Name:</strong> {searchData.tenant_info.company_name || "Not set"}</p>
                <p><strong>Address:</strong> {searchData.tenant_info.address || "Not set"}</p>
                <p><strong>Phone:</strong> {searchData.tenant_info.phone || "Not set"}</p>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm font-medium text-blue-800 mb-3">Steps to get your Google Business URL:</p>
            <ol className="text-sm text-blue-700 space-y-2">
              <li className="flex gap-2">
                <span className="font-bold">1.</span>
                <span>Open <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">Google Maps</a> in a new browser tab</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">2.</span>
                <span>Search for your business name: <strong>&quot;{searchData?.tenant_info?.company_name || 'Your Business'}&quot;</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">3.</span>
                <span>Click on your business listing in the results</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">4.</span>
                <span>Click the <strong>&quot;Share&quot;</strong> button, then <strong>&quot;Copy link&quot;</strong></span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold">5.</span>
                <span>Paste the URL below and click Save</span>
              </li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {/* Enter URL Card */}
      <Card className="border-2 border-zinc-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LinkIcon size={20} />
            Enter Your Business URL
          </CardTitle>
          <CardDescription>
            Paste the link to your Google Business listing or any review page
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Business Review URL</Label>
            <Input
              value={googleUrl}
              onChange={(e) => setGoogleUrl(e.target.value)}
              placeholder="https://..."
              className="mt-1 h-12 border-2"
              data-testid="google-url-input"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Enter any valid URL where customers can leave a review (Google Maps, Yelp, Facebook, etc.)
            </p>
          </div>
          <Button
            onClick={saveGoogleUrl}
            disabled={saving || !googleUrl.trim()}
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
            data-testid="save-google-url-btn"
          >
            {saving ? "Saving..." : "Save URL"}
          </Button>
        </CardContent>
      </Card>

      {/* Ready Section */}
      {searchData?.current_url && (
        <Card className="border-2 border-emerald-200 bg-emerald-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-800">
              <Star size={20} weight="fill" className="text-amber-500" />
              Ready to Collect Reviews!
            </CardTitle>
            <CardDescription className="text-emerald-700">
              Your review page is connected. You can now send review requests to customers.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-emerald-700 mb-4">
              When you complete a job, send a review request to the customer. They&apos;ll receive a link via their preferred contact method (email or SMS) to leave a review.
            </p>
            <Button variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-100" asChild>
              <a href="/customers">Go to Customers</a>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
